<template>
  <div class="monetary-panel overflow-hidden border border-gray-200 rounded-lg bg-white">
    <!-- Title bar -->
    <div class="monetary-panel__titlebar flex items-center justify-between gap-3 px-4 py-2.5">
      <h3 class="text-sm font-bold text-white uppercase tracking-wide m-0">
        {{ $t('monetary_executions_weekly_title') }}
      </h3>
      <div class="flex items-center gap-2 flex-shrink-0">
        <div class="monetary-panel__switch inline-flex rounded overflow-hidden border border-white border-opacity-40">
          <button
            type="button"
            class="monetary-panel__switch-btn"
            :class="{ 'is-active': progressMode === 'inspected' }"
            @click="progressMode = 'inspected'"
          >
            {{ $t('inspected') }}
          </button>
          <button
            type="button"
            class="monetary-panel__switch-btn"
            :class="{ 'is-active': progressMode === 'actual' }"
            @click="progressMode = 'actual'"
          >
            {{ $t('actual') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Sub-header: project badge + cutoff date -->
    <div class="flex items-center justify-between gap-3 px-4 py-2 bg-white border-b border-gray-200">
      <span class="monetary-panel__project-badge">
        {{ projectName || '—' }}
      </span>
      <div class="monetary-panel__cutoff inline-flex items-center gap-2 text-xs">
        <span class="uppercase tracking-wide text-grey-darker font-semibold">
          {{ $t('report_measurement_cutoff_date') }}:
        </span>
        <span class="monetary-panel__cutoff-date">
          {{ cutoffLabel }}
        </span>
      </div>
    </div>

    <!-- Summary bars (project totals) -->
    <div class="flex flex-wrap items-end gap-8 px-4 py-4 border-b border-gray-200 bg-grey-lightest">
      <div class="monetary-summary">
        <div class="monetary-summary__label">{{ $t('to_origin') }}</div>
        <div class="monetary-summary__bars">
          <div class="monetary-summary__bar-item">
            <div class="monetary-summary__pct">{{ formatPercent(summary.toOrigin.scheduledPct) }}</div>
            <div
              class="monetary-summary__bar"
              :style="{ height: summaryBarHeight(summary.toOrigin.scheduledPct, 56) }"
            />
            <div class="monetary-summary__caption">{{ $t('scheduled') }}</div>
            <div class="monetary-summary__amount">{{ formatAmount(summary.toOrigin.scheduled) }}</div>
          </div>
          <div class="monetary-summary__bar-item">
            <div class="monetary-summary__pct">{{ formatPercent(summary.toOrigin.executedPct) }}</div>
            <div
              class="monetary-summary__bar monetary-summary__bar--executed"
              :style="{ height: summaryBarHeight(summary.toOrigin.executedPct, 56) }"
            />
            <div class="monetary-summary__caption">{{ $t('executed1') }}</div>
            <div class="monetary-summary__amount">{{ formatAmount(summary.toOrigin.executed) }}</div>
          </div>
        </div>
      </div>

      <div class="monetary-summary">
        <div class="monetary-summary__label">{{ $t('current_month') }}</div>
        <div class="monetary-summary__bars">
          <div class="monetary-summary__bar-item">
            <div class="monetary-summary__pct">{{ formatPercent(summary.currentMonth.inMonthPct) }}</div>
            <div
              class="monetary-summary__bar"
              :style="{ height: summaryBarHeight(summary.currentMonth.inMonthPct, 44) }"
            />
            <div class="monetary-summary__caption">{{ $t('in_the_month') }}</div>
            <div class="monetary-summary__amount">{{ formatAmount(summary.currentMonth.inMonth) }}</div>
          </div>
          <div class="monetary-summary__bar-item">
            <div class="monetary-summary__pct">{{ formatPercent(summary.currentMonth.atCutoffPct) }}</div>
            <div
              class="monetary-summary__bar"
              :style="{ height: summaryBarHeight(summary.currentMonth.atCutoffPct, 44) }"
            />
            <div class="monetary-summary__caption">{{ $t('at_cutoff') }}</div>
            <div class="monetary-summary__amount">{{ formatAmount(summary.currentMonth.atCutoff) }}</div>
          </div>
          <div class="monetary-summary__bar-item">
            <div class="monetary-summary__pct">{{ formatPercent(summary.currentMonth.executedPct) }}</div>
            <div
              class="monetary-summary__bar monetary-summary__bar--executed"
              :style="{ height: summaryBarHeight(summary.currentMonth.executedPct, 44) }"
            />
            <div class="monetary-summary__caption">{{ $t('executed1') }}</div>
            <div class="monetary-summary__amount">{{ formatAmount(summary.currentMonth.executed) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="relative">
      <div v-if="isTableLoading" class="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
        <scale-loader />
      </div>
      <div class="overflow-x-auto">
        <table class="monetary-table w-full min-w-[1600px] text-sm">
          <thead>
            <tr class="text-[0.6875rem] uppercase tracking-wide">
              <th rowspan="3" class="monetary-table__sticky-col monetary-table__header px-3 py-2 font-bold text-left whitespace-nowrap">
                {{ $t('level_activity') }}
              </th>
              <th rowspan="3" class="monetary-table__header px-2 py-2 font-bold text-center whitespace-nowrap">
                {{ $t('unit_abbr') }}
              </th>
              <th rowspan="3" class="monetary-table__header px-3 py-2 font-bold text-right whitespace-nowrap">
                {{ $t('total') }}
              </th>
              <th colspan="6" class="monetary-table__section-header monetary-table__section-header--prev px-3 py-2 font-bold text-center whitespace-nowrap">
                {{ $t('origin_previous_month_close') }}
              </th>
              <th colspan="10" class="monetary-table__section-header monetary-table__section-header--month px-3 py-2 font-bold text-center whitespace-nowrap">
                {{ $t('current_month') }}
              </th>
              <th colspan="6" class="monetary-table__section-header monetary-table__section-header--origin px-3 py-2 font-bold text-center whitespace-nowrap">
                {{ $t('to_origin') }}
              </th>
            </tr>

            <tr class="text-[0.625rem] uppercase tracking-wide">
              <th colspan="2" class="monetary-table__subheader px-2 py-1.5 font-semibold text-center whitespace-nowrap">
                {{ $t('scheduled') }}
              </th>
              <th colspan="2" class="monetary-table__subheader monetary-table__subheader--executed px-2 py-1.5 font-semibold text-center whitespace-nowrap">
                {{ $t('executed1') }}
              </th>
              <th colspan="2" class="monetary-table__subheader monetary-table__subheader--deviation px-2 py-1.5 font-semibold text-center whitespace-nowrap">
                {{ $t('deviation') }}
              </th>
              <th colspan="4" class="monetary-table__subheader px-2 py-1.5 font-semibold text-center whitespace-nowrap">
                {{ $t('scheduled') }}
              </th>
              <th colspan="2" class="monetary-table__subheader monetary-table__subheader--executed px-2 py-1.5 font-semibold text-center whitespace-nowrap">
                {{ $t('executed1') }}
              </th>
              <th colspan="2" class="monetary-table__subheader monetary-table__subheader--deviation px-2 py-1.5 font-semibold text-center whitespace-nowrap">
                {{ $t('deviation') }}
              </th>
              <th colspan="2" class="monetary-table__subheader monetary-table__subheader--pending px-2 py-1.5 font-semibold text-center whitespace-nowrap">
                {{ $t('pending') }}
              </th>
              <th colspan="2" class="monetary-table__subheader px-2 py-1.5 font-semibold text-center whitespace-nowrap">
                {{ $t('scheduled') }}
              </th>
              <th colspan="2" class="monetary-table__subheader monetary-table__subheader--executed px-2 py-1.5 font-semibold text-center whitespace-nowrap">
                {{ $t('executed1') }}
              </th>
              <th colspan="2" class="monetary-table__subheader monetary-table__subheader--deviation px-2 py-1.5 font-semibold text-center whitespace-nowrap">
                {{ $t('deviation') }}
              </th>
            </tr>

            <tr class="text-[0.625rem] uppercase tracking-wide">
              <th class="monetary-table__subheader px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('amount_abbr') }}</th>
              <th class="monetary-table__subheader px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
              <th class="monetary-table__subheader monetary-table__subheader--executed px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('amount_abbr') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--executed px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
              <th class="monetary-table__subheader monetary-table__subheader--deviation px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('amount_abbr') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--deviation px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
              <th class="monetary-table__subheader px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('in_the_month') }}</th>
              <th class="monetary-table__subheader px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
              <th class="monetary-table__subheader px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('at_cutoff') }}</th>
              <th class="monetary-table__subheader px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
              <th class="monetary-table__subheader monetary-table__subheader--executed px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('amount_abbr') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--executed px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
              <th class="monetary-table__subheader monetary-table__subheader--deviation px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('amount_abbr') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--deviation px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
              <th class="monetary-table__subheader monetary-table__subheader--pending px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('amount_abbr') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--pending px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
              <th class="monetary-table__subheader px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('amount_abbr') }}</th>
              <th class="monetary-table__subheader px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
              <th class="monetary-table__subheader monetary-table__subheader--executed px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('amount_abbr') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--executed px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
              <th class="monetary-table__subheader monetary-table__subheader--deviation px-2 py-1.5 font-semibold text-right whitespace-nowrap">{{ $t('amount_abbr') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--deviation px-2 py-1.5 font-semibold text-right whitespace-nowrap">%</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!isTableLoading && !tableDisplayRows.length">
              <td colspan="25" class="px-4 py-8 text-center text-grey-dark">
                {{ $t('no_results') }}
              </td>
            </tr>

            <tr
              v-for="row in tableDisplayRows"
              :key="row.key"
              class="border-b border-gray-200"
              :class="row.isProject ? 'monetary-table__row--project' : (row.depth === 0 ? 'monetary-table__row--parent' : 'monetary-table__row--child')"
              :style="rowAccentStyle(row)"
            >
              <td class="monetary-table__sticky-col px-3 py-2 text-grey-darkest" :class="row.depth > 0 ? 'text-xs' : ''">
                <div
                  class="flex items-center gap-2 min-w-0"
                  :style="rowIndentStyle(row)"
                  :class="row.hasChildren ? 'cursor-pointer select-none' : ''"
                  @click="row.hasChildren ? toggleGroupExpand(row) : null"
                >
                  <b-icon
                    v-if="isRowLoadingChildren(row)"
                    pack="fas"
                    icon="sync-alt"
                    size="is-small"
                    custom-class="fa-spin text-grey flex-shrink-0"
                  />
                  <b-icon
                    v-else-if="row.hasChildren"
                    pack="fas"
                    :icon="isGroupExpanded(row.key) ? 'chevron-down' : 'chevron-right'"
                    size="is-small"
                    class="text-grey flex-shrink-0"
                  />
                  <span v-else class="w-3 flex-shrink-0" />
                  <span :class="rowLabelClass(row)">{{ row.label }}</span>
                </div>
              </td>
              <td class="px-2 py-2 text-center text-grey-dark whitespace-nowrap">{{ currencySymbol }}</td>
              <td class="px-3 py-2 text-right font-semibold text-grey-darkest whitespace-nowrap">{{ formatAmount(row.total) }}</td>

              <!-- Origen cierre mes anterior -->
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.prev.scheduled) }}</td>
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatPercent(row.prev.scheduledPct) }}</td>
              <td class="monetary-table__cell--executed px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.prev.executed) }}</td>
              <td class="monetary-table__cell--executed px-2 py-2 text-right whitespace-nowrap">{{ formatPercent(row.prev.executedPct) }}</td>
              <td
                class="monetary-table__cell--deviation px-2 py-2 text-right whitespace-nowrap"
                :class="deviationClass(row.prev.deviation)"
              >
                {{ formatSignedAmount(row.prev.deviation) }}
              </td>
              <td
                class="monetary-table__cell--deviation px-2 py-2 text-right whitespace-nowrap"
                :class="deviationClass(row.prev.deviationPct)"
              >
                {{ formatSignedPercent(row.prev.deviationPct) }}
              </td>

              <!-- Mes actual -->
              <td class="px-2 py-2 text-right whitespace-nowrap">
                {{ formatAmount(row.month.inMonth) }}
              </td>
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatPercent(row.month.inMonthPct) }}</td>
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.month.atCutoff) }}</td>
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatPercent(row.month.atCutoffPct) }}</td>
              <td class="monetary-table__cell--executed px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.month.executed) }}</td>
              <td class="monetary-table__cell--executed px-2 py-2 text-right whitespace-nowrap">{{ formatPercent(row.month.executedPct) }}</td>
              <td
                class="monetary-table__cell--deviation px-2 py-2 text-right whitespace-nowrap"
                :class="deviationClass(row.month.deviation)"
              >
                {{ formatSignedAmount(row.month.deviation) }}
              </td>
              <td
                class="monetary-table__cell--deviation px-2 py-2 text-right whitespace-nowrap"
                :class="deviationClass(row.month.deviationPct)"
              >
                {{ formatSignedPercent(row.month.deviationPct) }}
              </td>
              <td class="monetary-table__cell--pending px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.month.pending) }}</td>
              <td class="monetary-table__cell--pending px-2 py-2 text-right whitespace-nowrap">{{ formatPercent(row.month.pendingPct) }}</td>

              <!-- a Origen -->
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.origin.scheduled) }}</td>
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatPercent(row.origin.scheduledPct) }}</td>
              <td class="monetary-table__cell--executed px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.origin.executed) }}</td>
              <td class="monetary-table__cell--executed px-2 py-2 text-right whitespace-nowrap">{{ formatPercent(row.origin.executedPct) }}</td>
              <td
                class="monetary-table__cell--deviation px-2 py-2 text-right whitespace-nowrap"
                :class="deviationClass(row.origin.deviation)"
              >
                {{ formatSignedAmount(row.origin.deviation) }}
              </td>
              <td
                class="monetary-table__cell--deviation px-2 py-2 text-right whitespace-nowrap"
                :class="deviationClass(row.origin.deviationPct)"
              >
                {{ formatSignedPercent(row.origin.deviationPct) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import dashboardPanelTreeMixin, { pct, sumField, appendVisibleRows } from './dashboard-panel-tree-mixin'

function emptyPeriodAmounts() {
  return {
    total: 0,
    scheduled: 0,
    executed: 0,
    executed_actual: 0,
  }
}

export default {
  name: 'DashboardSubpanelMonetaryExecutions',

  components: {
    ScaleLoader,
  },

  mixins: [dashboardPanelTreeMixin],

  computed: {
    groupRows() {
      return (this.rootGroups || []).map((group) => this.buildRowFromGroup(group, 0))
    },

    projectTotalsRow() {
      const roots = this.groupRows
      if (!roots.length) {
        return null
      }

      const total = sumField(roots, (r) => r.total)
      const prevScheduled = sumField(roots, (r) => r.prev.scheduled)
      const prevExecuted = sumField(roots, (r) => r.prev.executed)
      const inMonth = sumField(roots, (r) => r.month.inMonth)
      const atCutoff = sumField(roots, (r) => r.month.atCutoff)
      const monthExecuted = sumField(roots, (r) => r.month.executed)

      return this.buildDerivedRow({
        key: 'project',
        id: 'project',
        label: this.$t('project').toUpperCase(),
        depth: 0,
        isProject: true,
        hasChildren: false,
        children: [],
        total,
        prevScheduled,
        prevExecuted,
        inMonth,
        atCutoff,
        monthExecuted,
      })
    },

    tableDisplayRows() {
      const rows = []
      if (this.projectTotalsRow) {
        rows.push(this.projectTotalsRow)
      }
      this.groupRows.forEach((group) => {
        appendVisibleRows(group, rows, (key) => this.isGroupExpanded(key))
      })
      return rows
    },

    summary() {
      const row = this.projectTotalsRow
      if (!row) {
        return {
          toOrigin: { scheduled: null, scheduledPct: null, executed: null, executedPct: null },
          currentMonth: {
            inMonth: null,
            inMonthPct: null,
            atCutoff: null,
            atCutoffPct: null,
            executed: null,
            executedPct: null,
          },
        }
      }

      return {
        toOrigin: {
          scheduled: row.origin.scheduled,
          scheduledPct: row.origin.scheduledPct,
          executed: row.origin.executed,
          executedPct: row.origin.executedPct,
        },
        currentMonth: {
          inMonth: row.month.inMonth,
          inMonthPct: row.month.inMonthPct,
          atCutoff: row.month.atCutoff,
          atCutoffPct: row.month.atCutoffPct,
          executed: row.month.executed,
          executedPct: row.month.executedPct,
        },
      }
    },
  },

  methods: {
    fetchNodeChildren(parentId) {
      return this.fetchThreePeriods(parentId)
    },

    indexGroupsById(groups) {
      const map = {}
      ;(groups || []).forEach((g) => {
        map[g.id] = g
      })
      return map
    },

    async fetchPeriod(parentGroupId, start, end) {
      const params = {
        location_id: 0,
        status_id: 1,
        start: start || undefined,
        end: end || undefined,
      }

      if (parentGroupId !== null && parentGroupId !== undefined) {
        params.parent_group_id = parentGroupId
      }

      const url = `/api/v2/project/${this.projectId}/dashboard/monetaryexecutions`
      const { data } = await axios.get(url, { params })
      const payload = data && data.data ? data.data : {}
      return Array.isArray(payload.groups) ? payload.groups : []
    },

    async fetchThreePeriods(parentGroupId) {
      const { prevMonthEnd, monthStart, monthEnd, cutoff, hasCutoffInCurrentMonth } = this.periodDates

      const requests = [
        this.fetchPeriod(parentGroupId, null, prevMonthEnd),
        this.fetchPeriod(parentGroupId, monthStart, monthEnd),
      ]

      if (hasCutoffInCurrentMonth) {
        requests.push(this.fetchPeriod(parentGroupId, monthStart, cutoff))
      } else {
        requests.push(Promise.resolve([]))
      }

      const [prevGroups, fullMonthGroups, cutoffGroups] = await Promise.all(requests)
      return this.mergePeriodGroups(prevGroups, fullMonthGroups, cutoffGroups)
    },

    mergePeriodGroups(prevGroups, fullMonthGroups, cutoffGroups) {
      const prevMap = this.indexGroupsById(prevGroups)
      const fullMap = this.indexGroupsById(fullMonthGroups)
      const cutoffMap = this.indexGroupsById(cutoffGroups)

      const ids = new Set([
        ...Object.keys(prevMap),
        ...Object.keys(fullMap),
        ...Object.keys(cutoffMap),
      ])

      return Array.from(ids).map((idKey) => {
        const id = Number(idKey) || idKey
        const prev = prevMap[id] || emptyPeriodAmounts()
        const full = fullMap[id] || emptyPeriodAmounts()
        const cut = cutoffMap[id] || emptyPeriodAmounts()
        const base = prevMap[id] || fullMap[id] || cutoffMap[id] || {}

        return {
          id: base.id || id,
          name: base.name || String(id),
          children_count: Number(
            (base.children_count !== undefined
              ? base.children_count
              : (prevMap[id] || fullMap[id] || cutoffMap[id] || {}).children_count) || 0
          ),
          total: Number(base.total) || Number(full.total) || Number(prev.total) || Number(cut.total) || 0,
          prev: {
            scheduled: Number(prev.scheduled) || 0,
            executed: Number(prev.executed) || 0,
            executed_actual: Number(prev.executed_actual) || 0,
          },
          month: {
            inMonthScheduled: Number(full.scheduled) || 0,
            atCutoffScheduled: Number(cut.scheduled) || 0,
            executed: Number(cut.executed) || 0,
            executed_actual: Number(cut.executed_actual) || 0,
          },
        }
      })
    },

    buildDerivedRow({
      key,
      id,
      label,
      depth,
      isProject,
      hasChildren,
      children,
      total,
      prevScheduled,
      prevExecuted,
      inMonth,
      atCutoff,
      monthExecuted,
    }) {
      const prevDeviation = prevExecuted - prevScheduled
      const monthDeviation = monthExecuted - atCutoff
      const pending = inMonth - monthExecuted
      const originScheduled = prevScheduled + atCutoff
      const originExecuted = prevExecuted + monthExecuted
      const originDeviation = originExecuted - originScheduled

      return {
        key,
        id,
        label,
        depth,
        isProject: Boolean(isProject),
        hasChildren: Boolean(hasChildren),
        children: children || [],
        total,
        prev: {
          scheduled: prevScheduled,
          scheduledPct: pct(prevScheduled, total),
          executed: prevExecuted,
          executedPct: pct(prevExecuted, total),
          deviation: prevDeviation,
          deviationPct: pct(prevDeviation, total),
        },
        month: {
          inMonth,
          inMonthPct: pct(inMonth, total),
          atCutoff,
          atCutoffPct: pct(atCutoff, total),
          executed: monthExecuted,
          executedPct: pct(monthExecuted, total),
          deviation: monthDeviation,
          deviationPct: pct(monthDeviation, total),
          pending,
          pendingPct: pct(pending, total),
        },
        origin: {
          scheduled: originScheduled,
          scheduledPct: pct(originScheduled, total),
          executed: originExecuted,
          executedPct: pct(originExecuted, total),
          deviation: originDeviation,
          deviationPct: pct(originDeviation, total),
        },
      }
    },

    buildRowFromGroup(group, depth) {
      const prevExecuted = this.progressMode === 'actual'
        ? group.prev.executed_actual
        : group.prev.executed
      const monthExecuted = this.progressMode === 'actual'
        ? group.month.executed_actual
        : group.month.executed

      const children = (this.childrenByParentId[group.id] || [])
        .map((child) => this.buildRowFromGroup(child, depth + 1))

      return this.buildDerivedRow({
        key: String(group.id),
        id: group.id,
        label: group.name || String(group.id),
        depth,
        isProject: false,
        hasChildren: Number(group.children_count || 0) > 0,
        children,
        total: group.total,
        prevScheduled: group.prev.scheduled,
        prevExecuted,
        inMonth: group.month.inMonthScheduled,
        atCutoff: group.month.atCutoffScheduled,
        monthExecuted,
      })
    },

  },
}
</script>

<style scoped src="./monetary-panel.css"></style>
